/**
 * /help — public-facing FAQ + troubleshooting tips.
 *
 * No auth required. Search filters both lists simultaneously.
 * Deep-link friendly via section hash anchors (#faq, #troubleshooting).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, AlertCircle, Lightbulb } from 'lucide-react';
import { FAQ, TROUBLESHOOTING } from './content';
import { FaqRow } from './helpers';

export function Help() {
  const [q, setQ] = useState('');
  const location = useLocation();

  const fFaq = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return FAQ;
    return FAQ.filter(f => f.q.toLowerCase().includes(needle) || f.a.toLowerCase().includes(needle));
  }, [q]);

  const fTrb = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return TROUBLESHOOTING;
    return TROUBLESHOOTING.filter(t => t.q.toLowerCase().includes(needle) || t.a.toLowerCase().includes(needle));
  }, [q]);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }, [location.hash]);

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <div className="mb-6 text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">HELP CENTER</div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">How can we help?</h1>
        <p className="text-fg-muted mt-2">FAQ + troubleshooting tips. Can&apos;t find what you need? Email <a href="mailto:support@physiogenic.pl" className="text-brand">support@physiogenic.pl</a>.</p>
      </div>

      <div className="relative mb-8">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"/>
        <input type="search" value={q} onChange={e=>setQ(e.target.value)}
               placeholder="Search FAQs…" className="input pl-9 text-base" data-testid="help-search"/>
      </div>

      <section id="faq" className="mb-10">
        <h2 className="text-xl font-heading font-bold mb-4 inline-flex items-center gap-2"><Lightbulb className="w-5 h-5 text-brand"/> Frequently asked</h2>
        <div className="space-y-2" data-testid="faq-list">
          {fFaq.map((f, i) => <FaqRow key={i} f={f} testid={`faq-q-${i}`}/>)}
          {fFaq.length === 0 && <p className="text-fg-muted text-sm">No FAQs match.</p>}
        </div>
      </section>

      <section id="troubleshooting">
        <h2 className="text-xl font-heading font-bold mb-4 inline-flex items-center gap-2"><AlertCircle className="w-5 h-5 text-[#FFB000]"/> Troubleshooting</h2>
        <div className="space-y-2" data-testid="troubleshooting-list">
          {fTrb.map((f, i) => <FaqRow key={i} f={f} testid={`ts-q-${i}`}/>)}
          {fTrb.length === 0 && <p className="text-fg-muted text-sm">No troubleshooting topics match.</p>}
        </div>
      </section>
    </div>
  );
}
